// src/components/HomePage.jsx
import React, { useEffect, useState } from "react";
import "./adminEventManagement.css";
import { supabase } from '../../supabase_client.jsx';

export function getApprovedEvents(events, today) {
  if (!events) return [];
  return events
    .filter(e => e.is_approved === true && new Date(e.date) >= today)
    .sort((a, b) => new Date(a.date) - new Date(b.date));
}

export function getPendingEvents(events) {
  if (!events) return [];
  return events
    .filter(e => e.is_approved !== true)
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
}

export function getReportedEvents(events) {
  if (!events) return [];
  return events.filter(e => e.is_reported === true);
}

export function getPastEvents(events, today) {
  if (!events) return [];
  return events
    .filter(e => e.is_approved === true && new Date(e.date) < today)
    .sort((a, b) => new Date(b.date) - new Date(a.date));
}

export default function AdminEventManagement({ onViewSelect, onAddEvent, editEvent: editEventProp }) {
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [editEvent, setEditEvent] = useState(null);
  const [events, setEvents] = useState(null);
  const [formData, setFormData] = useState({});
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);

  const [expandedLists, setExpandedLists] = useState({
    approved: false,
    pending: false,
    reported: false,
    past: false
  });

  const today = new Date();
  
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      console.log("=== SAVE DEBUG ===");
      console.log("formData:", formData);
      console.log("editEvent.id:", editEvent.id);
      console.log("editEvent current location:", editEvent.location);
      
      const { data, error } = await supabase
        .from("events")
        .update(formData)
        .eq("id", editEvent.id)
        .select();

      if (error) {
        console.error("Save error:", error);
        alert("Save failed: " + error.message);
        return;
      }

      console.log("Update response:", data);
      console.log("=== END SAVE DEBUG ===");
      
      await getEvents();
      setEditEvent(null);
      setFormData({});
    } catch (err) {
      console.error("Unexpected error:", err);
      alert("Unexpected error: " + err.message);
    }
  };

  const handleSaveAndApprove = async () => {
    try {
      console.log("=== APPROVE DEBUG ===");
      console.log("formData:", formData);
      console.log("Adding is_approved: true");
      
      const updateData = {...formData, is_approved: true};
      console.log("Final update data:", updateData);
      
      const { data, error } = await supabase
        .from("events")
        .update(updateData)
        .eq("id", Number(editEvent.id))
        .select();
      
      if (error) {
        console.error("Approve error:", error);
        alert("Approve failed: " + error.message);
        return;
      }

      console.log("Approve response:", data);
      console.log("=== END APPROVE DEBUG ===");

      await getEvents();
      setEditEvent(null);
      setFormData({});
    } catch (err) {
      console.error("Unexpected error:", err);
      alert("Unexpected error: " + err.message);
    }
  };

  const handleUnpublish = async () => {
    try {
      console.log("=== UNPUBLISH DEBUG ===");
      console.log("formData:", formData);
      console.log("Setting is_approved: false");

      const updateData = {...formData, is_approved: false};
      console.log("Final update data:", updateData);

      const { data, error } = await supabase
        .from("events")
        .update(updateData)
        .eq("id", Number(editEvent.id))
        .select();

      if (error) {
        console.error("Unpublish error:", error);
        alert("Unpublish failed: " + error.message);
        return;
      }

      console.log("Unpublish response:", data);
      console.log("=== END UNPUBLISH DEBUG ===");

      await getEvents();
      setEditEvent(null);
      setFormData({});

    } catch (err) {
      console.error("Unexpected error:", err);
      alert("Unexpected error: " + err.message);
    }
  };

  const handleDelete = async () => {
    try {
      console.log("=== DELETE DEBUG ===");
      console.log("Deleting event ID:", editEvent.id);

      const { error } = await supabase
        .from("events")
        .delete()
        .eq("id", Number(editEvent.id));

      if (error) {
        console.error("Delete error:", error);
        alert("Delete failed: " + error.message);
        return;
      }

      console.log("Delete successful");
      console.log("=== END DELETE DEBUG ===");

      await getEvents();
      setEditEvent(null);
      setFormData({});
      setDeleteConfirmation(false);
    } catch (err) {
      console.error("Unexpected error:", err);
      alert("Unexpected error: " + err.message); 
    }
  };

  async function getEvents() {
    const { data, error } = await supabase
      .from("events")
      .select("*");

      if (error) {
        console.error("Error fetching events: ", error);
      } else {
        setEvents(data);
      }
  }

  useEffect(() => {
    getEvents();
  }, []);

  useEffect(() => {
    if (editEventProp) {
      setEditEvent(editEventProp);
      setFormData({});
    }
  }, [editEventProp]);

  const getApprovedEvents = (events) => {
    if (!events) return [];
    return events
      .filter(event => event.is_approved === true && new Date(event.date) >= today)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const getPendingEvents = (events) => {
    if (!events) return [];
    return events
      .filter(event => event.is_approved !== true)
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at)); // oldest submitted at top, newest at bottom
  };

  const getReportedEvents = (events) => {
    if (!events) return [];
    return events.filter(
      (event) => event.is_reported === true
    );
  };

  const getPastEvents = (events) => {
    if (!events) return [];
    return events
      .filter(event => event.is_approved === true && new Date(event.date) < today)
      .sort((a, b) => new Date(b.date) - new Date(a.date)); // most recently past at top
  };

  const renderEventList = (events, listType) => {
    if (!events || events.length === 0) {
      return <p className="empty-state">No events</p>
    }

    const isExpanded = expandedLists[listType];
    const visibleEvents = isExpanded ? events: events.slice(0, 5);
    const extraCount = events.length - 5;

      return (
        <div className="event-list-wrapper">
          {visibleEvents.map(event => (
            <div key={event.id} className="event-item" onClick={() => setSelectedEvent(event)}>
            <div className="event-text">
              <span className="event-name">{event.name}</span>
              <span className="event-date">{event.date}</span>
            </div>

            <span
              className="edit-icon"
              onClick={(e) => {
                e.stopPropagation();
                setFormData({
                  name: event.name,
                  date: event.date,
                  start_time: event.start_time,
                  end_time: event.end_time,
                  location: event.location,
                  description: event.description,
                  is_approved: event.is_approved
                });
                setEditEvent(event);
              }}
            >
              ✎
            </span>
            </div>
          ))}

          {!isExpanded && extraCount > 0 && (
            <div
              className="expand-toggle"
              onClick={() => setExpandedLists(prev => ({...prev, [listType]: true}))} 
            >
              + {extraCount} more events
            </div>
          )}

          {isExpanded && extraCount > 0 && (
            <div
              className="collapse-toggle"
              onClick={() => setExpandedLists(prev => ({...prev, [listType]: false}))}
            >
              Collapse
            </div>
          )}

        </div>
      );
  };


  return (
      <div className="event-management-container">
        <div className="home-card">
          {/* header */}
          <div className="calendar-top-nav">
              <div className="nav-left">
                  <button className="nav-tab" onClick={() => onViewSelect('admin')}>
                      General
                  </button>
                  <button className="nav-tab" onClick={() => onViewSelect('adminCalendar')}>
                      Event Calendar
                  </button>
                  <button className="nav-tab" onClick={() => onViewSelect('adminKPIDash')}>
                    KPI Dashboard
                  </button>
                  <button className="nav-tab active">Event Management</button>
                  <button className="nav-tab" onClick={() => onViewSelect('user-management')}>
                    User Management
                  </button>
                  <button className="nav-tab" onClick={() => onViewSelect('business-management')}>
                    Business Management
                  </button>
                  <button className="bpActionBtn secondary" onClick={() => {
                    sessionStorage.removeItem("currentUser");
                    sessionStorage.removeItem("currentView");
                    onViewSelect("loginSignup");
                  }}>
                    Log Out
                  </button>
              </div>
          </div>

          <div className="add-event-container">
              <button 
                className="add-event-button" 
                onClick={() => onAddEvent()}
              >
                + Add Event
              </button>
          </div>
          <div className="event-blocks-container">
              <div className="left-column">
                  {/* approved events */}
                  <div className="approved-events event-block">
                      <h3>Approved Events</h3>
                      {renderEventList(getApprovedEvents(events), "approved")}
                  </div>
                  <div className="event-block">
                    <h3>Past Events</h3>
                    {renderEventList(getPastEvents(events), "past")}
                  </div>
              </div>
              <div className="right-column">
                  {/* pending events */}
                  <div className="pending-events event-block">
                      <h3>Pending Events</h3>
                      {renderEventList(getPendingEvents(events), "pending")}
                  </div>

                  {/* past events */}
                  <div className="reported-events event-block">
                      <h3>Reported Events</h3>
                      {renderEventList(getReportedEvents(events))}
                  </div>
              </div>
          </div>

          {/*event details popup */}
          {selectedEvent && (
            <div className="popup-overlay" onClick={() => setSelectedEvent(null)}>
              <div className="popup" onClick={(e) => e.stopPropagation()}>
                <button className="close-btn" onClick={() => setSelectedEvent(null)}>✕</button>

                {selectedEvent.image_url ? (
                  <img src={selectedEvent.image_url} alt={selectedEvent.name} className="popup-image" />
                ) : (
                  <div className="detail-image-placeholder">No Image</div>
                )}

                <div className="popup-content">
                <div className="popup-header" style={{ marginTop: 16 }}>
                  <div>
                    <h2 className="popup-title">{selectedEvent.name}</h2>
                    <span className={`popup-badge ${selectedEvent.is_approved ? 'badge-approved' : 'badge-pending'}`}>
                      {selectedEvent.is_approved ? 'Approved' : 'Pending'}
                    </span>
                  </div>
                </div>

                <div className="popup-divider" />

                <div className="popup-details">
                  {selectedEvent.date && (
                    <div className="popup-row">
                      <span className="popup-label">Date</span>
                      <span className="popup-value">{selectedEvent.date}</span>
                    </div>
                  )}
                  {(selectedEvent.start_time || selectedEvent.end_time) && (
                    <div className="popup-row">
                      <span className="popup-label">Time</span>
                      <span className="popup-value">
                        {selectedEvent.start_time}{selectedEvent.end_time ? ` – ${selectedEvent.end_time}` : ''}
                      </span>
                    </div>
                  )}
                  {selectedEvent.location && (
                    <div className="popup-row">
                      <span className="popup-label">Location</span>
                      <span className="popup-value">{selectedEvent.location}</span>
                    </div>
                  )}
                  {selectedEvent.event_owner && (
                    <div className="popup-row">
                      <span className="popup-label">Event Owner</span>
                      <span className="popup-value">{selectedEvent.event_owner}</span>
                    </div>
                  )}
                  {selectedEvent.description && (
                    <div className="popup-row popup-row-col">
                      <span className="popup-label">Description</span>
                      <span className="popup-value" style={{ textAlign: 'left' }}>{selectedEvent.description}</span>
                    </div>
                  )}
                </div>
                </div>
              </div>
            </div>
          )}

          {/** edit event popup */}
          {editEvent && (
            <div className="admin-modal-overlay">
              <div className="admin-modal">

                {/*close button */}
                <button
                  className="admin-close-btn"
                  onClick={() => setEditEvent(null)}
                >
                  ✕
                </button>

                {/* Header */}
                <div className="admin-modal-header">
                  <h2>{editEvent.name}</h2>
                  <span className="admin-business">
                    {editEvent.business_name || "Business Name"}
                  </span>
                </div>

                {/* Body */}
                <div className="admin-modal-body">

                  {/* Left column */}
                  <div className="admin-left">
                    <div className="admin-image-box">
                      {editEvent.image_url ? (
                        <img src={editEvent.image_url} alt={editEvent.name} />
                      ) : (
                        <div className="admin-image-placeholder">
                          Event Photo
                        </div>
                      )}
                    </div>

                    <div className="admin-meta-grid">
                      <div className="admin-field">
                        <label>Date</label>
                        <input 
                          type="date" 
                          value={formData.date ?? editEvent.date ?? ''}
                          onChange={(e) => handleChange("date", e.target.value)}
                        />
                      </div>

                      <div className="admin-field">
                        <label>Start Time</label>
                        <input 
                          type="time" 
                          value={formData.start_time ?? editEvent.start_time ?? ''} 
                          onChange={(e) => handleChange("start_time", e.target.value)}
                        />
                      </div>

                      <div className="admin-field">
                        <label>End Time</label>
                        <input 
                          type="time" 
                          value={formData.end_time ?? editEvent.end_time ?? ''}
                          onChange={(e) => handleChange("end_time", e.target.value)}
                        />
                      </div>

                      <div className="admin-field">
                        <label>Location</label>
                        <input 
                          type="text"
                          value={formData.location ?? editEvent.location ?? ''}
                          onChange={(e) => handleChange("location", e.target.value)} 
                        />
                      </div>
                    </div>
                  </div>

                  {/* Right column */}
                  <div className="admin-right">
                    <div className="admin-field">
                      <label>Event Owner</label>
                      <input 
                        type="text" 
                        value={formData.event_owner ?? editEvent.event_owner ?? ''} 
                        onChange={(e) => handleChange("event_owner", e.target.value)}
                      />
                    </div>
                    <div className="admin-field">
                      <label>Description</label>
                      <textarea 
                        value={formData.description ?? editEvent.description ?? ''} 
                        onChange={(e) => handleChange("description", e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* save / approve */}
                <div className="admin-modal-footer">
                  {editEvent.is_approved ? (
                    // approved events
                    <>
                      <button 
                        className="admin-save-btn"
                        onClick={handleSave}
                      >
                        Save
                      </button>

                      <button
                        className="admin-unpublish-btn"
                        onClick={handleUnpublish}
                      >
                        Unpublish
                      </button>
                    </>
                  ) : (
                    // pending/reported events
                    <>
                      <button
                        className="admin-save-btn"
                        onClick={handleSave}
                      >
                        Save
                      </button>
                      <button 
                        className="admin-approve-btn"
                        onClick={handleSaveAndApprove}
                      >
                        Save & Approve
                      </button>
                    </>
                  )}

                  {/**delete for all events */}
                  <button
                    className="admin-delete-btn"
                    onClick={() => setDeleteConfirmation(true)}
                  >
                    Delete
                  </button>

                </div>

              </div>
            </div>
          )}

          {/**delete confirmation */}
          {deleteConfirmation && (
            <div className="admin-modal-overlay">
              <div className="admin-modal admin-delete-confirmation">
                <h2>Delete Event</h2>
                <p>Are you sure you want to delete this event?</p>
                <div className="admin-modal-footer">
                  <button
                    className="admin-cancel-btn"
                    onClick={() => setDeleteConfirmation(false)}
                  >
                    No
                  </button>
                  <button
                    className="admin-confirm-delete-btn"
                    onClick={handleDelete}
                  >
                    Yes
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
  );
}